import AxiosHttpClient from 'app/api/AxiosHttpClient';
import { CardProduct } from 'app/model/product/CardProduct';
import { ResourceFormat } from 'app/model/ResourceFormat';
import { ResourceService } from 'app/service/ResourceService';
import { ConvertingMap } from 'app/utils/ConvertingMap';
import { RkkCardProduct } from 'app/model/product/RkkCardProduct';
import { KkgCardProduct } from 'app/model/product/KkgCardProduct';
import { CalculatorProps } from './types/creditCalculator';

/**
 * Сервис работы со счетами пользователя
 */
export class ProductService {
    private apiClient: AxiosHttpClient = new AxiosHttpClient();

    private controller = new AbortController();

    private resourceService: ResourceService = new ResourceService(
        this.apiClient,
    );

    private DEFAULT_CHANNEL = '0147';

    private readonly getCardProductPath = (id: string) =>
        `/products/cards/${id}`;

    private readonly getInsuranceInstructionsPath = (
        productCode: string,
        documentCode: string,
    ) => `/products/insurance/${productCode}/documents/${documentCode}`;

    private readonly getRestrictionsPath = (productCode: string) =>
        `/products/cards/${productCode}/restrictions`;

    private readonly getCalculatorPath = (productCode: string) =>
        `/products/cards/${productCode}/calculator`;

    private readonly PRODUCT_TYPE_TO_CARD_PRODUCT: ConvertingMap<
        string,
        (data: any) => CardProduct
    > = new ConvertingMap<string, (data: any) => CardProduct>(
        [
            ['RKK', RkkCardProduct.deserialize],
            ['KKG', KkgCardProduct.deserialize],
        ],
        CardProduct.deserialize,
    );

    /**
     * Возвращает данные о продукте карты
     *
     * @param id идентификатор
     * @param channel канал продаж
     */
    async getCardProduct(id: string, channel: string): Promise<CardProduct> {
        const data = await this.apiClient.executeGetRequest(
            this.getCardProductPath(id),
            { channel: channel || this.DEFAULT_CHANNEL },
        );
        return this.PRODUCT_TYPE_TO_CARD_PRODUCT.convert(data.code as string)(
            data,
        );
    }

    /**
     * Получить параметры кредитного калькулятора
     * @param productCode
     * @param sum
     */
    async getCreditCalculator(
        productCode: string,
        sum?: number,
    ): Promise<CalculatorProps> {
        this.controller.abort(
            'Abort previous calculator request: new sum requested',
        );

        return await this.apiClient.executeGetRequest(
            this.getCalculatorPath(productCode),
            { sum, signal: this.controller.signal },
        );
    }

    /**
     * Получить документ описывающий преимуществами страхования
     * @param format формат документа
     * @param productCode код продукта страхования
     * @param documentCode код документа
     */
    async getInsuranceInstructions(
        productCode: string,
        documentCode: string,
        format: ResourceFormat,
    ) {
        return this.resourceService.downloadResource(
            this.getInsuranceInstructionsPath(productCode, documentCode),
            format,
        );
    }

    /**
     * Проверка на наличие ограничений по использованию СДБО
     * @param productCode код продукта
     * @param orderId ID заявки
     * @returns вернет CreditCardOrderRejectedException в случае наличия запрета
     */
    async checkRestrictions(
        productCode: string,
        orderId: string,
    ): Promise<void> {
        return this.apiClient.executeGetRequest(
            this.getRestrictionsPath(productCode),
            { orderId },
        );
    }
}
